/* eslint-disable react/prop-types */
import { UploadDropZone } from '@rpldy/upload-drop-zone';
import {
  Uploady,
  FileFilterMethod,
  useBatchFinishListener,
  useUploady,
  Batch,
  BatchItem,
} from '@rpldy/uploady';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import attachmentGlowIcon from '../../../assets/images/icons/attachment-glow.svg';
import attachmentIcon from '../../../assets/images/icons/attachment.svg';
import cloudUploadIcon from '../../../assets/images/icons/cloud-upload-icon.svg';
import loadingIcon from '../../../assets/images/icons/loader-dark.svg';
import removeOptionIcon from '../../../assets/images/icons/trash-dark.svg';
import alert from '../../../utils/alert';
import { returnFileSize } from '../../account/components/avatar/avatarSectionHelpers';

const MAX_FILE_SIZE = 20000000; // 20 MB
const FILE_SIZE_UNIT = 1000000; // 1 MB

type Props = {
  onFileUpload?: (fileData: Array<{ file: File | Blob; name: string }>) => void;
  clearUploadedFiles?: boolean;
  showAttachmentIcon?: boolean;
  isSubmitting?: boolean;
};

const MessageAttachmentDropzone: React.FC<Props> = ({
  onFileUpload,
  clearUploadedFiles = false,
  showAttachmentIcon = false,
  isSubmitting = false,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { showFileUpload } = useUploady();
  const { t } = useTranslation();
  const [hoveredAttachment, setHoveredAttachment] = useState<string[]>([]);

  useEffect(() => {
    if (clearUploadedFiles) {
      setUploadedFiles([]);
    }
  }, [clearUploadedFiles]);

  useEffect(() => {
    if (uploadedFiles.length === 0) return;
    const attachments = uploadedFiles.map((file) => ({ file, name: file.name }));
    onFileUpload?.(attachments);
  }, [uploadedFiles, onFileUpload]);

  const handleBatchFinish = useCallback(
    (batch: Batch) => {
      const files = batch.items
        .filter((item): item is BatchItem & { file: File } => item.file instanceof File)
        .map((item) => item.file);

      const totalFiles = [...uploadedFiles, ...files];
      const batchSize = totalFiles.reduce((acc, item) => acc + item.size, 0);

      if (batchSize > MAX_FILE_SIZE) {
        alert.error(
          t(
            `The total attachments size exceeds the limit of ${Math.floor(MAX_FILE_SIZE / FILE_SIZE_UNIT)} MB`,
          ),
          { toastId: 'attachment-size-error' },
        );
        return;
      }
      setUploadedFiles(totalFiles);
    },
    [uploadedFiles, t],
  );

  useBatchFinishListener(handleBatchFinish);

  const handleAttachmentIconHover = useCallback(() => {
    setHoveredAttachment(uploadedFiles.map((file) => file.name));
  }, [uploadedFiles]);

  const fileFilter: FileFilterMethod = useCallback((file) => {
    if (typeof file !== 'string') {
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/bmp',
        'image/tiff',
        'application/pdf',
      ];
      return allowedTypes.includes(file.type);
    }
    return false;
  }, []);

  const handleShowFileUpload = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      showFileUpload({
        clearPendingOnAdd: true,
        maxGroupSize: 5,
        fileFilter,
        inputFieldName: 'message-attachment-upload-btn',
      });
    },
    [showFileUpload, fileFilter],
  );

  const removeFile = useCallback((index: number) => {
    setUploadedFiles((prevState) =>
      prevState.filter((unusedFile, fileIndex) => fileIndex !== index),
    );
  }, []);

  const renderAttachmentIcon = useMemo(
    () => (
      <div className='position-relative'>
        {uploadedFiles.length > 0 && (
          <span className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary'>
            {uploadedFiles.length}
          </span>
        )}
        <OverlayTrigger
          rootClose={false}
          trigger={['hover', 'focus']}
          placement='top'
          overlay={
            <Tooltip id='attachment-icon-tooltip'>
              {hoveredAttachment.length > 0
                ? hoveredAttachment.map((name, index) => <div key={index}>{name}</div>)
                : t('No attachment')}
            </Tooltip>
          }
        >
          <button
            className='bg-white border border-1 rounded-2 mb-0'
            onMouseEnter={handleAttachmentIconHover}
            onMouseLeave={() => setHoveredAttachment([])}
            onClick={handleShowFileUpload}
            type='button'
            disabled={isSubmitting}
          >
            <img src={attachmentIcon} alt='upload indicator' />
          </button>
        </OverlayTrigger>
      </div>
    ),
    [
      uploadedFiles.length,
      hoveredAttachment,
      handleAttachmentIconHover,
      handleShowFileUpload,
      isSubmitting,
      t,
    ],
  );

  const renderUploadDropZone = useMemo(
    () => (
      <UploadDropZone
        id='upload-message-attachment-dropzone'
        className='custom__upload-modal-dropzone'
        onDragOverClassName='custom__upload-modal-dropzone--dragover'
        fileFilter={fileFilter}
      >
        <img src={cloudUploadIcon} alt='upload indicator' />
        <div className='d-flex flex-row align-items-center'>
          <button
            className='text-primary-700 p-0 bg-transparent border-0 h-100'
            onClick={handleShowFileUpload}
          >
            {t('Click to upload')}
          </button>
          &nbsp;
          <p className='m-0'>{t('or drag and drop')}</p>
        </div>
        <p className='text-gray-500 mb-0'>
          {t(`Image or PDF (combined max. ${Math.floor(MAX_FILE_SIZE / FILE_SIZE_UNIT)} MB)`)}
        </p>
      </UploadDropZone>
    ),
    [fileFilter, handleShowFileUpload, t],
  );

  return (
    <>
      {showAttachmentIcon ? renderAttachmentIcon : renderUploadDropZone}
      <div className='custom__upload-attachments d-none'>
        {uploadedFiles.map((uploadedFile, index) => (
          <div className='col p-2' key={index}>
            <div className='custom__upload-modal-file-container'>
              <div className='d-flex' style={{ maxWidth: '90%' }}>
                <img src={attachmentGlowIcon} alt='attachment icon' />
                <div className='ps-2' style={{ height: 'fit-content', overflow: 'hidden' }}>
                  <p
                    className='text-gray-700 text-truncate custom__upload-attachments-title'
                    style={{ maxWidth: '180px' }}
                    title={uploadedFile.name}
                  >
                    {t(uploadedFile.name)}
                  </p>
                  <p className='text-gray-500 custom__upload-attachments-size'>
                    {returnFileSize(uploadedFile.size)}
                  </p>
                </div>
              </div>
              <button
                className={`border-0 bg-transparent p-0 ${isSubmitting ? '' : 'mb-auto'}`}
                onClick={() => removeFile(index)}
                disabled={isSubmitting}
                type='button'
              >
                {isSubmitting ? (
                  <img src={loadingIcon} alt='loading' className='spin' />
                ) : (
                  <img src={removeOptionIcon} alt='remove' />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const MessageAttachments: React.FC<Props> = (props) => (
  <Uploady
    multiple
    accept='image/jpeg,image/jpg,image/png,image/bmp,image/tiff,application/pdf'
    inputFieldName='message-attachment-field'
    fileInputId='message-attachment-field-input'
  >
    <MessageAttachmentDropzone {...props} />
  </Uploady>
);

export default MessageAttachments;
